import { type ReactNode, useCallback, useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { clsx } from 'clsx'
import { supabase } from '@/lib/supabase/client'
import {
  Menu,
  X,
  ArrowLeft,
  LayoutDashboard,
  ClipboardCheck,
  FileText,
  LogOut,
  ScanSearch,
} from 'lucide-react'
import { getNdtExitPath } from '@/utils/ndtNavigation'
import { PageHeaderProvider, type PageHeaderOverride } from './pageHeader'

interface NavItem {
  label: string
  icon: React.ComponentType<{ size?: number; className?: string; strokeWidth?: number }>
  href: string
  matchPaths?: string[]
}

const WLL_YELLOW = '#FFBB00'

const NDT_METHOD_PATHS = ['/et-test', '/mt-test', '/vt-test', '/pt-test', '/lt-test', '/ut-test', '/rt-test', '/thread-test']
const NDT_FORM_PATHS = [...NDT_METHOD_PATHS, '/service-report']

const navItems: NavItem[] = [
  { label: 'Dashboard', icon: LayoutDashboard, href: '/dashboard' },
  {
    label: 'Løft',
    icon: ClipboardCheck,
    href: '/loft-selection',
    matchPaths: ['/loft-selection', '/inspect', '/samleliste', '/service-report'],
  },
  {
    label: 'NDT',
    icon: ScanSearch,
    href: '/ndt-selection',
    matchPaths: ['/ndt-selection', ...NDT_METHOD_PATHS],
  },
  { label: 'Rapporter', icon: FileText, href: '/reports' },
]

function titleFromPath(pathname: string): string {
  if (pathname === '/dashboard') return 'Dashboard'
  if (pathname === '/loft-selection') return 'Velg type'
  if (pathname === '/ndt-selection') return 'Velg NDT-type'
  if (pathname === '/et-test') return 'Eddy Current Testing'
  if (pathname === '/mt-test') return 'Magnetic Particle Testing'
  if (pathname === '/vt-test') return 'Visual Testing'
  if (pathname === '/pt-test') return 'Penetrant Testing'
  if (pathname === '/lt-test') return 'Load Test'
  if (pathname === '/ut-test') return 'Ultrasonic Testing'
  if (pathname === '/rt-test') return 'Radiographic Testing'
  if (pathname === '/thread-test') return 'Gjengeinspeksjon'
  if (pathname === '/service-report') return 'Servicerapport'
  if (pathname === '/company') return 'Velg selskap'
  if (pathname === '/equipment') return 'Velg utstyr'
  if (pathname.startsWith('/inspect')) return 'Inspeksjon'
  if (pathname === '/reports') return 'Dokumentasjon'
  if (pathname === '/samleliste-flow') return 'Kontroll av løfteutstyr'
  if (pathname.startsWith('/samleliste/')) return 'Periodisk kontroll'
  return ''
}

interface AppShellProps {
  children: ReactNode
}

const AppShell = ({ children }: AppShellProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [pageHeader, setPageHeader] = useState<PageHeaderOverride>(null)
  const setOverride = useCallback((value: PageHeaderOverride) => setPageHeader(value), [])

  const title = pageHeader?.title || titleFromPath(location.pathname)
  const subtitle = pageHeader?.subtitle
  const hideAppHeader = !!pageHeader?.hideAppHeader
  const showBack = !hideAppHeader && (pageHeader?.showBack ?? (!!pageHeader?.onBack || location.pathname !== '/dashboard'))

  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname, hideAppHeader])

  const handleDefaultBack = () => {
    const path = location.pathname
    if (path === '/loft-selection' || path === '/ndt-selection' || path === '/reports') {
      navigate('/dashboard')
      return
    }
    if (NDT_FORM_PATHS.includes(path)) {
      navigate(getNdtExitPath())
      return
    }
    if (path === '/company') {
      const inspectionType = sessionStorage.getItem('inspectionType')
      if (inspectionType === 'ndt') navigate(getNdtExitPath())
      else if (inspectionType === 'loft') {
        navigate(sessionStorage.getItem('loftExitPath') === '/dashboard' ? '/dashboard' : '/loft-selection')
      } else navigate('/dashboard')
      return
    }
    if (path === '/equipment') {
      navigate('/company')
      return
    }
    if (path.startsWith('/inspect')) {
      navigate('/equipment')
      return
    }
    if (path === '/samleliste-flow') {
      navigate('/loft-selection')
      return
    }
    if (path.startsWith('/samleliste/')) {
      navigate('/samleliste-flow')
      return
    }
    navigate(-1)
  }

  const handleBack = () => {
    if (pageHeader?.onBack) pageHeader.onBack()
    else handleDefaultBack()
  }

  const handleLogout = async () => {
    await supabase.auth.signOut()
    navigate('/login')
  }

  const isActive = (item: NavItem) => {
    if (item.matchPaths) {
      return item.matchPaths.some((p) => location.pathname.startsWith(p))
    }
    return location.pathname === item.href
  }

  return (
    <div className="flex flex-col h-dvh overflow-hidden" style={{ backgroundColor: 'var(--bg-primary)' }}>

      {!hideAppHeader && (
        <header
          className="flex items-center shrink-0 px-3 gap-1 z-50 select-none"
          style={{
            height: 56,
            backgroundColor: 'var(--color-primary)',
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          <button
            onClick={() => setMenuOpen((open) => !open)}
            className="md:hidden flex items-center justify-center rounded-md shrink-0"
            style={{ height: 44, width: 44, color: WLL_YELLOW }}
            title={menuOpen ? 'Lukk meny' : 'Meny'}
            aria-label={menuOpen ? 'Lukk meny' : 'Åpne meny'}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X size={26} strokeWidth={2.4} /> : <Menu size={26} strokeWidth={2.4} />}
          </button>

          {showBack && (
            <button
              onClick={handleBack}
              className="flex items-center justify-center rounded-md shrink-0 text-white/75 hover:bg-white/10 active:bg-white/15 transition-colors"
              style={{ height: 48, width: 48 }}
              title="Tilbake"
              aria-label="Tilbake"
            >
              <ArrowLeft size={24} />
            </button>
          )}

          <div className="flex-1 min-w-0 px-1 md:px-2">
            <p className="text-base md:text-lg font-semibold text-white truncate leading-tight">{title}</p>
            {subtitle && (
              <p className="text-xs md:text-sm truncate leading-tight" style={{ color: 'rgba(255,255,255,0.6)' }}>
                {subtitle}
              </p>
            )}
          </div>

          <button
            onClick={handleLogout}
            className="md:hidden flex items-center gap-1.5 px-3 rounded-md transition-colors text-sm font-medium shrink-0 text-white/60 hover:bg-white/10 hover:text-white/90 active:bg-white/15"
            style={{ height: 44, minWidth: 44 }}
            title="Logg ut"
          >
            <LogOut size={18} />
            <span className="hidden sm:inline">Logg ut</span>
          </button>
        </header>
      )}

      {!hideAppHeader && menuOpen && (
        <>
          <button
            type="button"
            className="md:hidden fixed left-0 right-0 bottom-0 z-40"
            style={{ backgroundColor: 'rgba(15,23,42,0.35)', top: 56 }}
            aria-label="Lukk meny"
            onClick={() => setMenuOpen(false)}
          />
          <nav
            className="md:hidden fixed left-0 z-50 w-64 max-w-[80vw] shadow-lg overflow-hidden"
            style={{
              top: 56,
              backgroundColor: 'var(--color-primary)',
              borderRight: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            {navItems.map((item) => {
              const Icon = item.icon
              const active = isActive(item)
              return (
                <button
                  key={item.href}
                  onClick={() => {
                    setMenuOpen(false)
                    navigate(item.href)
                  }}
                  className={clsx(
                    'w-full flex items-center gap-3 px-5 text-left transition-colors',
                    active ? 'bg-white/10 font-semibold' : 'text-white/75 font-medium hover:bg-white/5 active:bg-white/10',
                  )}
                  style={{ height: 56, color: active ? WLL_YELLOW : undefined }}
                  aria-current={active ? 'page' : undefined}
                >
                  <Icon size={20} />
                  <span className="text-base">{item.label}</span>
                </button>
              )
            })}
          </nav>
        </>
      )}

      <div className="flex flex-1 min-h-0">
        {!hideAppHeader && (
          <nav
            aria-label="Hovednavigasjon"
            className="hidden md:flex flex-col items-stretch shrink-0 w-[var(--rail-width)] py-3 gap-1 select-none"
            style={{
              backgroundColor: 'var(--color-primary)',
              borderRight: '1px solid rgba(255,255,255,0.08)',
              paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))',
            }}
          >
            {navItems.map((item) => {
              const Icon = item.icon
              const active = isActive(item)
              return (
                <button
                  key={item.href}
                  onClick={() => navigate(item.href)}
                  className="group flex flex-col items-center justify-center gap-1 py-2 min-h-[72px]"
                  aria-current={active ? 'page' : undefined}
                >
                  <span
                    className={clsx(
                      'flex items-center justify-center w-14 h-8 rounded-full transition-colors',
                      active ? 'text-[#1E3A5F]' : 'text-white/75 group-hover:bg-white/10 group-active:bg-white/15',
                    )}
                    style={active ? { backgroundColor: WLL_YELLOW } : undefined}
                  >
                    <Icon size={22} strokeWidth={active ? 2.4 : 2} />
                  </span>
                  <span
                    className={clsx('text-xs leading-tight', active ? 'font-semibold' : 'font-medium text-white/75')}
                    style={active ? { color: WLL_YELLOW } : undefined}
                  >
                    {item.label}
                  </span>
                </button>
              )
            })}

            <button
              onClick={handleLogout}
              className="group mt-auto flex flex-col items-center justify-center gap-1 py-2 min-h-[72px] text-white/60"
              title="Logg ut"
            >
              <span className="flex items-center justify-center w-14 h-8 rounded-full transition-colors group-hover:bg-white/10 group-active:bg-white/15">
                <LogOut size={20} />
              </span>
              <span className="text-xs font-medium leading-tight">Logg ut</span>
            </button>
          </nav>
        )}

        <main className="flex-1 min-w-0 min-h-0 overflow-y-auto flex flex-col overscroll-none">
          <PageHeaderProvider setOverride={setOverride}>
            {children}
          </PageHeaderProvider>
        </main>
      </div>

    </div>
  )
}

export default AppShell
